/**
 * UserEditPage - Form for editing an existing user (admin only).
 *
 * Route: /admin/users/:id
 */

import { useParams, useNavigate } from '@tanstack/react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'

import { useAuth } from '@/common/hooks/useAuth'
import { useToast } from '@/common/hooks/useToast'
import { getErrorMessage } from '@/common/utils/errors'
import { LoadingSpinner } from '@/common/components/loading-spinner'
import { Text } from '@/common/components/text'
import { Icon } from '@/common/components/icon'
import { Button } from '@/common/components/button'

import { getUser, updateUser } from '@/features/admin-users/api/adminUsers'
import { UserForm } from '@/features/admin-users/components/UserForm'
import * as styles from '@/features/admin-users/components/UserForm/UserForm.css'

export const Route = createFileRoute({
  component: UserEditPage,
})

function UserEditPage() {
  const { id } = useParams({ strict: false })
  const navigate = useNavigate()
  const { token } = useAuth()
  const queryClient = useQueryClient()
  const { showSuccessToast, showErrorToast } = useToast()

  // Fetch user data
  const {
    data: user,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['admin-user', id],
    queryFn: () => getUser(token!, id!),
    enabled: !!token && !!id,
  })

  // Reset wholesale block mutation
  const resetWholesaleMutation = useMutation({
    mutationFn: () => updateUser(token!, id!, { wholesale_intent: '' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-user', id] })
      queryClient.invalidateQueries({ queryKey: ['admin-users'] })
      showSuccessToast('Wholesale block has been reset. The investor can now re-attempt onboarding.')
    },
    onError: (err) => {
      showErrorToast(getErrorMessage(err) || 'Failed to reset wholesale block')
    },
  })

  const handleSuccess = () => {
    navigate({ to: '/admin/users' })
  }

  const handleCancel = () => {
    navigate({ to: '/admin/users' })
  }

  // Loading state
  if (isLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          <LoadingSpinner size="lg" />
        </div>
      </div>
    )
  }

  // Error state or user is admin (cannot edit)
  const isAdminUser = user?.roles.includes('admin')

  if (error || !user || isAdminUser) {
    return (
      <div className={styles.container}>
        <div className={styles.errorContainer}>
          <Icon
            name={isAdminUser ? 'lock' : 'error_outline'}
            size={48}
            color="muted"
          />
          <Text as="h2" variant="bodyLg" weight="bold">
            {isAdminUser ? 'Cannot Edit Admin User' : 'User Not Found'}
          </Text>
          <Text variant="body" color="muted" style={{ maxWidth: 400 }}>
            {isAdminUser
              ? 'Admin user accounts cannot be modified through this interface.'
              : "The user you're trying to edit doesn't exist or you don't have permission to edit them."}
          </Text>
          <Button onClick={() => navigate({ to: '/admin/users' })}>
            Back to Users
          </Button>
        </div>
      </div>
    )
  }

  const isWholesaleBlocked = user.wholesale_intent === 'yes' && !user.onboarding_completed_at

  return (
    <>
      {isWholesaleBlocked && (
        <div className={styles.container} style={{ paddingBottom: 0 }}>
          <div className={styles.card} style={{ borderColor: '#e8a735', backgroundColor: '#fef9ee', marginBottom: 0, padding: '12px 16px' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
              <Icon name="warning" size={20} style={{ color: '#b5850a', flexShrink: 0, marginTop: 2 }} />
              <div style={{ flex: 1 }}>
                <Text variant="body" weight="bold" style={{ marginBottom: 4 }}>
                  Wholesale Blocked
                </Text>
                <Text variant="bodySm" color="muted" style={{ marginBottom: 12 }}>
                  This investor answered &quot;Yes&quot; to wholesale intent and is blocked from completing onboarding.
                  Resetting will clear their answer and allow them to re-attempt onboarding.
                </Text>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => resetWholesaleMutation.mutate()}
                  disabled={resetWholesaleMutation.isPending}
                >
                  {resetWholesaleMutation.isPending ? 'Resetting...' : 'Reset Wholesale Block'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      <UserForm
        mode="edit"
        initialData={user}
        onSuccess={handleSuccess}
        onCancel={handleCancel}
      />
    </>
  )
}
